/**
 * Helpers for de-duplicating concurrent async work keyed by some identifier
 * (document id, request body, entity id, ...). Two complementary strategies are
 * provided because call sites want different semantics:
 *
 * - `createInFlightKeySet`  — *skip* duplicate work (e.g. ignore a double-click).
 *   A second concurrent call resolves to `undefined` without running `fn`.
 * - `createInFlightPromiseCache` — *share* a single result. Concurrent callers
 *   receive the same in-flight promise so the underlying work runs once.
 *
 * Both release the key once the operation settles (success or failure), so a
 * later call always hits the network again.
 */

export interface InFlightKeySet<K> {
  has: (key: K) => boolean;
  add: (key: K) => void;
  delete: (key: K) => void;
  /**
   * Runs `fn` unless `key` is already in flight, in which case it resolves to
   * `undefined` without invoking `fn`. The key is always released afterwards.
   */
  run: <T>(key: K, fn: () => Promise<T>) => Promise<T | undefined>;
}

export function createInFlightKeySet<K>(): InFlightKeySet<K> {
  const keys = new Set<K>();

  return {
    has: (key) => keys.has(key),
    add: (key) => {
      keys.add(key);
    },
    delete: (key) => {
      keys.delete(key);
    },
    run: async <T>(key: K, fn: () => Promise<T>): Promise<T | undefined> => {
      if (keys.has(key)) {
        return undefined;
      }

      keys.add(key);
      try {
        return await fn();
      } finally {
        keys.delete(key);
      }
    },
  };
}

export interface InFlightPromiseCache<K, V> {
  run: (key: K, fn: () => Promise<V>) => Promise<V>;
}

export function createInFlightPromiseCache<K, V>(): InFlightPromiseCache<K, V> {
  const pending = new Map<K, Promise<V>>();

  return {
    run: (key, fn) => {
      const existing = pending.get(key);

      if (existing) {
        return existing;
      }

      const run = (async (): Promise<V> => {
        try {
          return await fn();
        } finally {
          pending.delete(key);
        }
      })();

      pending.set(key, run);

      return run;
    },
  };
}
